import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X, Send, MessageCircle, BookOpen, ArrowLeft } from 'lucide-react';

interface KBEntry {
  keywords: string[];
  answer: string;
}

const KNOWLEDGE_BASE: KBEntry[] = [
  {
    keywords: ['what', 'can', 'do', 'about', 'agentomy', 'overview', 'platform', 'what is'],
    answer:
      'Agentomy is a governance platform for AI agents in regulated enterprises. Every agent action flows through a 10-stage governance pipeline producing SHA-256 hash-linked forensic evidence. The platform provides 5 identity tiers, cryptographic audit trails, a fleet-wide kill switch, compliance mapping across 12 frameworks, and multi-model orchestration. You govern what your agents can do across their entire lifecycle.',
  },
  {
    keywords: ['govern', 'agent', 'how', 'manage', 'control', 'orchestrat'],
    answer:
      'Govern your agents by assigning identity tiers (External Evaluator, Analyst, Builder, Operator, Strategist), which control what each agent is authorized to do. Use POST /api/claw/authorize to register and authorize agents (every call flows through the 10-stage governance pipeline), POST /api/claw/log to record governance events, and POST /api/claw/halt to halt the entire fleet immediately. Every action produces a per-stage forensic evidence chain.',
  },
  {
    keywords: ['tier', 'identity', 'access', 'role', 'permission', 'level'],
    answer:
      'Agentomy uses 5 identity tiers:\n\n- Evaluator: Read-only with enhanced audit logging. For external reviewers and auditors. Full visibility, cannot change anything.\n- Analyst: Read-only operational access. Can query, list, search, and check status.\n- Builder: Standard read and write access. The working tier for development and integration.\n- Operator: Elevated access. Can execute, deploy, configure, and manage. Cannot override governance.\n- Strategist: Full access including halt, resume, and override. The highest authority tier.\n\nEach tier maps to specific API endpoint permissions. The governance pipeline enforces tier checks at Stage 3 (Authorization).',
  },
  {
    keywords: ['pipeline', 'stage', 'evidence', 'forensic', '10-stage', 'chain'],
    answer:
      'The governance pipeline has 10 stages that fire on every /authorize call:\n\n1. Identity - verify agent identity\n2. Trust - validate trust chain and score\n3. Authorization - tier-based permission check\n4. Behavioral Check - anomaly detection against rolling baseline\n5. Content Input - scan for credentials, injection, malicious content\n6. Action Execution - the actual agent action\n7. Output Scanning - IP scrub, compliance verification, policy check\n8. Evidence Recording - hash-linked forensic audit chain written to PostgreSQL\n9. Drift Update - feed action into behavioral baseline\n10. Halt Evaluation - cascade halt if any stage flagged\n\nEach stage produces a SHA-256 hash-linked finding. The entire chain is independently verifiable via POST /api/pipeline/verify.',
  },
  {
    keywords: ['api', 'endpoint', 'rest', 'route', 'url', 'http'],
    answer:
      'Agentomy exposes 6 core governance endpoints:\n\n- POST /api/claw/authorize - Authorize an agent action through the 10-stage pipeline\n- POST /api/claw/log - Record a governance event\n- POST /api/claw/halt - Emergency halt all agents (kill switch)\n- POST /api/claw/resume - Resume halted agents\n- GET /api/claw/status/:agentId - Agent governance status\n- GET /api/claw/health - System health\n\nPipeline endpoints:\n- GET /api/pipeline/stages - List all 10 pipeline stages\n- POST /api/pipeline/verify - Verify evidence chain integrity\n\nAll endpoints require API key authentication via X-API-Key header.',
  },
  {
    keywords: ['mcp', 'tool', 'claude', 'protocol', 'model context'],
    answer:
      'Agentomy provides 12 MCP (Model Context Protocol) tools:\n\n**Core (5 -- standalone):**\n- agentomy_authorize - Authorize through governance pipeline\n- agentomy_log - Log a governance event\n- agentomy_status - Check agent status\n- agentomy_halt - Emergency halt\n- agentomy_prompt_verify - System prompt integrity check\n\n**Fleet (7 -- with coordinator):**\n- agentomy_drift_score - Get behavioral drift score\n- agentomy_exposure_profile - Get agent exposure profile\n- agentomy_shadow_summary - Shadow AI discovery summary\n- agentomy_vigil_run - Run adversarial benchmark\n- agentomy_vigil_report - Get benchmark report\n- agentomy_vigil_status - Benchmark status\n- agentomy_self_audit - Run governance self-audit\n\nMCP integration requires 4 lines of config.',
  },
  {
    keywords: ['kill', 'halt', 'stop', 'emergency', 'switch', 'shutdown', 'pause'],
    answer:
      'The kill switch provides immediate emergency halt of all agents:\n\n- POST /api/claw/halt - Halts all active agents instantly. Requires Operator or Strategist tier. Requires operator confirmation string.\n- POST /api/claw/resume - Resumes agents after review. Also requires elevated tier.\n\nThe pipeline Stage 10 (Halt Evaluation) can also trigger fleet-level halt when critical failures cascade. All halt/resume events are recorded in the audit trail with full attribution.',
  },
  {
    keywords: ['compliance', 'soc', 'hipaa', 'eu ai', 'iso', 'pci', 'nist', 'framework', 'regulation', 'gdpr', 'sox', 'ferpa'],
    answer:
      // Every figure below is the length of the corresponding evidence map in
      // api/compliance-evidence-generator.js (or listMappedArticles() for the EU AI
      // Act), so this answer cannot drift from what getAvailableFrameworks() reports.
      // It previously claimed 8 frameworks, "SOC 2 - 49 controls (3 implemented, 26
      // partial, 20 gap)", and "ISO 27001 - 93 Annex A controls" -- none of which any
      // enumeration in this repository produced.
      'Agentomy maps governance controls to 12 compliance frameworks, 9 at self-assessed readiness and 3 control-mapped and planned:\n\n- SOC 2 - 43 Trust Services Criteria mapped: all 33 Common Criteria (CC1.1-CC9.2) plus Availability, Confidentiality, Processing Integrity\n- HIPAA Security Rule - 15 controls mapped\n- EU AI Act - 8 articles mapped (9 through 15, plus Article 50 transparency)\n- ISO 27001:2022 - 15 Annex A controls mapped\n- PCI DSS - 12 requirements mapped\n- GDPR - 14 articles mapped, Article 22 rights served at /api/gdpr\n- NIST SP 800-53 Rev. 5 - 15 controls mapped (FedRAMP baseline; authorization not initiated)\n- MITRE ATLAS - 11 tactics mapped at official ATLAS identifiers\n- NIST AI RMF - 29 subcategories mapped across Govern, Map, Measure, Manage\n- NIST PQC, SOX, FERPA - control-mapped and planned\n\nThese are self-assessed readiness mappings, not third-party certification. Governance reports are exported as human-readable narratives for compliance review. The 10-stage pipeline evidence chain provides per-request proof for auditors.',
  },
  {
    keywords: ['audit', 'trail', 'log', 'history', 'record', 'tamper', 'hash', 'chain'],
    answer:
      'Every governance call produces a SHA-256 hash-linked evidence chain stored in PostgreSQL. The 10-stage pipeline records per-stage findings with:\n\n- Stage number, name, and status (pass/flag/halt)\n- Duration in milliseconds\n- SHA-256 hash linking to the previous stage\n- Timestamp\n\nThe chain is independently verifiable: call POST /api/pipeline/verify with the findings array to confirm no tampering. The Audit Trail view in the Command Center shows entries with expandable pipeline evidence.',
  },
  {
    keywords: ['sdk', 'integrate', 'install', 'setup', 'python', 'javascript', 'js', 'start', 'getting started', 'onboard'],
    answer:
      'Integration paths:\n\n- SDK: npm install @agentomy/governance, then 3 lines to govern any agent\n- API: 6 REST endpoints for direct HTTP integration\n- MCP: 4-line config for any MCP-compatible agent runtime\n- Docker: docker compose up for the full platform\n\nThe fastest path is MCP integration (4 lines of config). For full control, use the REST API or SDK. The governance pipeline fires automatically on every /authorize call.',
  },
  {
    keywords: ['chat', 'conversation', 'talk', 'ask', 'message', 'llm'],
    answer:
      'The Chat tab provides multi-session conversations with real LLM responses routed through the governance pipeline. Every chat interaction is governed. Chat sessions persist across browser reloads.\n\nFor platform help questions, you are in the right place. For governed AI conversations, use the Chat tab.',
  },
  {
    keywords: ['model', 'openrouter', 'kimi', 'qwen', 'deepseek', 'mistral', 'multi-model', 'routing'],
    answer:
      'Agentomy routes to multiple AI models through OpenRouter. Model routing is governed by data classification tiers (RED/ORANGE/YELLOW/GREEN) that determine which models can access which data. The governance layer is model-agnostic -- it governs behavior regardless of which model executes the work.',
  },
  {
    keywords: ['agent', 'peer', 'embedded', 'agentomy agent', 'skill', 'system prompt', 'inside', 'what is'],
    answer:
      'Agentomy Agent is a governance peer that runs inside your agent team. No Docker container. No API calls. No infrastructure. No accounts.\n\nYou copy a system prompt into your agent session. The governance peer automatically begins observing every interaction, assessing governance posture, flagging risks in real time, and scoring your session against GovernanceBench -- six dimensions of what good agent governance looks like.\n\nIt does not slow down your agents. It does not block actions. It does not modify behavior. It sits alongside your agent and watches.\n\nNo competitor has deployed governance as an agent peer. Every other governance product sits outside the agent team. Agentomy Agent sits inside. In standalone mode, it can see and score but cannot cryptographically prove. In connected mode, it becomes a forensic instrument.',
  },
  {
    keywords: ['install', 'agent', 'setup', 'copy', 'prompt', '30 seconds'],
    answer:
      'Install Agentomy Agent in 30 seconds:\n\n1. Find the Agentomy Agent system prompt in the repository or at agentomy.com/agent.\n2. Copy the system prompt into your agent\'s session context.\n3. Start your agent session. The governance peer auto-loads and begins observing.\n\nNo config files. No dependencies. No accounts. No API keys for standalone mode. Works with any agent framework that accepts a system prompt.',
  },
  {
    keywords: ['score', '3/6', '6/6', 'standalone', 'connected', 'mode', 'meaning'],
    answer:
      '3/6 (standalone): The governance peer can observe, flag, and score. It cannot cryptographically attest or enforce. This is the ceiling for standalone mode -- observation alone cannot prove anything to a third party. A 3/6 is not a failure. It is the accurate score for a system that can see but cannot prove.\n\n6/6 (connected): The governance peer connects to the Agentomy infrastructure via MCP. Now it can cryptographically record every governance decision, verify evidence chains, and produce audit-grade proof. Observation becomes evidence.\n\nThe 3/6 to 6/6 gap is the built-in conversion mechanism. The agent honestly tells you what extra assurance you get by connecting the infrastructure.',
  },
  {
    keywords: ['governancebench', 'dimension', 'five', 'authorization', 'auditability', 'override', 'behavioral', 'owasp'],
    answer:
      'GovernanceBench measures governance posture across six dimensions:\n\n1. Authorization: Is the agent operating within its permitted boundaries? Does it have explicit permission for every action?\n2. Auditability: Does the session produce a reviewable record of what happened and why? Application logs are not an audit trail.\n3. Override: Can a human intervene, halt, or redirect the agent at any point? One command, confirmed halt.\n4. Behavioral: Is the agent staying within expected guardrails? Is there a behavioral baseline and drift detection?\n5. OWASP Coverage: Are known agent security risks (prompt injection, excessive agency, data leakage) addressed?\n6. Message Governance: Are agent-to-agent messages governed as actions? Instruction-override and escalation payloads refused, encoding is not a bypass, and every message is audited under a named policy version.\n\nEach dimension is scored separately. A session can score well on Authorization and still score low on Override.',
  },
  {
    keywords: ['session', 'summary', 'report', 'output', 'example', 'looks like'],
    answer:
      'A typical session summary from standalone mode:\n\nGovernanceBench Session Summary\nOverall score: 3/6 (Standalone mode)\n\nAuthorization: PASS -- Agent operated within declared scope.\nAuditability: PARTIAL -- Actions observable but not cryptographically recorded. Connect infrastructure for audit-grade proof.\nOverride: FLAG -- No kill-switch mechanism detected. Human intervention path is undefined.\nBehavioral: PASS -- Agent behavior consistent with declared purpose.\nOWASP Coverage: PARTIAL -- No prompt injection protections detected in input handling.\n\nFlags raised: 2 | Address Override and OWASP items before production deployment.\n\nEvery flag is specific. Every recommendation points to a concrete action. The score is honest.',
  },
  {
    keywords: ['standalone', 'enough', 'when', 'awareness', 'evaluate'],
    answer:
      'Standalone is enough when:\n\n- You want visibility into what your agents are doing before committing to infrastructure.\n- You are evaluating whether your governance posture has gaps worth addressing.\n- You want a second pair of eyes on agent behavior during development.\n- You are building internal confidence before rolling out to a team or customer.\n- You want to present a GovernanceBench score to demonstrate you have thought about governance, even before infrastructure is in place.\n\nStandalone gives you awareness. For many teams, awareness is the right first step.',
  },
  {
    keywords: ['connect', 'infrastructure', 'when', 'upgrade', 'proof', 'regulator', 'auditor'],
    answer:
      'Connect the infrastructure when you need to prove something to someone outside your team:\n\n- A regulator or auditor requires documented governance -- not a session score, but a cryptographic audit trail they can verify independently.\n- A customer or enterprise partner asks for proof of your AI governance posture during security review.\n- You need the kill switch -- one command that halts every agent in the fleet.\n- You need tier-based access control across your agent fleet.\n- You are operating agents in production at scale and need a tamper-evident record of every governance decision.\n- You need compliance framework mapping for EU AI Act, SOC 2, HIPAA, ISO 27001, or PCI DSS.',
  },
  {
    keywords: ['fooled', 'attack', 'adversarial', 'limit', 'honest', 'weakness', 'can\'t'],
    answer:
      'Can the governance peer be fooled? Yes. It is a system prompt, not a security perimeter. A sufficiently adversarial prompt could attempt to override it. The governance peer flags persona-override attempts, but a sophisticated attacker who knows the system prompt exists can design around it. Connected mode addresses this with infrastructure-level enforcement that does not depend on the model reading a system prompt correctly.\n\nDoes it slow down agents? No. It observes. It does not intercept or modify the execution path.\n\nDoes a 3/6 mean my governance is bad? No. A 3/6 is the accurate score for standalone mode. If your use case requires proof, 3/6 tells you exactly that you need to connect the infrastructure.',
  },
  {
    keywords: ['multi-agent', 'coordinator', 'sub-agent', 'spawn', 'team'],
    answer:
      'In multi-agent sessions where a coordinator agent spawns sub-agents, the governance peer observes coordinator and sub-agent behavior independently. It flags when a coordinator attempts to escalate a sub-agent\'s permissions. The infrastructure level provides hard enforcement -- the coordinator cannot actually execute an unauthorized sub-agent action.',
  },
  {
    keywords: ['dashboard', 'ui', 'tab', 'panel', 'interface', 'navigate', 'command center', 'view'],
    answer:
      'The Command Center has 8 views:\n\n- Fleet Overview: Agent status, pipeline stage visualization (10-stage grid), quarantine and steering status\n- Audit Trail: Hash-linked evidence chain with expandable pipeline findings per entry\n- Steer Console: Behavioral steering thresholds and fleet control\n- Discovery: Shadow AI detection across your infrastructure\n- Compliance: 8 regulatory frameworks mapped\n- Security Status: 166 governance patterns, anomaly detection, system health\n- GDPR Rights: Data subject request management\n- Explainability: Governance decision transparency',
  },
  {
    keywords: ['test', 'benchmark', 'governancebench', 'vigil', 'kevlar', 'owasp'],
    answer:
      '20,477 automated tests across the full stack (Jest 9,701, Python 10,126, Go 219, GovernanceBench 235, VIGIL 148, Kevlar 48). Governance benchmarks:\n\n- GovernanceBench: 235/235 across 6 dimensions (Authorization, Auditability, Override, Behavioral, OWASP Coverage, Message Governance) + 12 domain suites\n- VIGIL: 148 adversarial scenarios across 14 threat categories\n- Kevlar: OWASP ASI Top 10 coverage (10/10 defended)\n\n500 concurrent requests verified: 0 errors, sub-millisecond pipeline latency, 988 requests per second.',
  },
];

const FALLBACK_ANSWER =
  "I don't have a specific answer for that. For a complete walkthrough, see the Getting Started guide in the docs/ folder. Or try asking in the main Chat tab for a detailed answer.";

const QUICK_ACTIONS = [
  'What can Agentomy do?',
  'How does the governance pipeline work?',
  'What are the identity tiers?',
  'Show me the API endpoints',
  'How does the kill switch work?',
  'What compliance frameworks are supported?',
  'How does the audit trail work?',
  'What MCP tools are available?',
  'How do I integrate and get started?',
  'What are the test and benchmark results?',
  'What does the Command Center show?',
  'How does multi-model routing work?',
  'What is the Agentomy Agent?',
  'How do I install the Agentomy Agent?',
  'What do the governance scores mean?',
  'What are the GovernanceBench dimensions?',
  'What does a session summary look like?',
  'When is standalone mode enough?',
  'When should I connect the infrastructure?',
  'Can the governance peer be fooled?',
  'How does it work with multi-agent teams?',
  'Tell me about the Chat feature',
  'What is the evidence chain?',
];

function findAnswer(query: string): string {
  const lower = query.toLowerCase();
  let bestMatch: KBEntry | null = null;
  let bestScore = 0;

  for (const entry of KNOWLEDGE_BASE) {
    let score = 0;
    for (const kw of entry.keywords) {
      if (lower.includes(kw)) {
        score++;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = entry;
    }
  }

  return bestScore >= 1 ? bestMatch!.answer : FALLBACK_ANSWER;
}

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

const GUIDES = [
  { id: 'platform', title: 'Getting Started with Agentomy', description: 'Full platform guide: install, pipeline, tiers, compliance', url: '/api/docs/getting-started' },
  { id: 'agent', title: 'Getting Started with Agentomy Agent', description: 'The governance peer: install, scores, dimensions, FAQ', url: '/api/docs/getting-started-agent' },
];

export default function AskAgentomy() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [guideContent, setGuideContent] = useState<string | null>(null);
  const [guideTitle, setGuideTitle] = useState('');
  const [guideLoading, setGuideLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openGuide = async (guide: typeof GUIDES[0]) => {
    setGuideLoading(true);
    setGuideTitle(guide.title);
    try {
      const res = await fetch(guide.url);
      if (res.ok) {
        const text = await res.text();
        setGuideContent(text);
      } else {
        setGuideContent('Unable to load guide. The API may not be connected.');
      }
    } catch {
      setGuideContent('Unable to load guide. The API may not be connected.');
    }
    setGuideLoading(false);
  };

  const handleSubmit = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;

    const answer = findAnswer(trimmed);
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: trimmed },
      { role: 'assistant', text: answer },
    ]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(input);
    }
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#C2714F] hover:bg-[#D4825F] text-white shadow-lg shadow-[#7A3E28]/40 flex items-center justify-center transition-all duration-200 hover:scale-105"
        aria-label="Ask Agentomy"
        title="Ask Agentomy"
      >
        {isOpen ? <X className="w-6 h-6" /> : <HelpCircle className="w-7 h-7" />}
      </button>

      {/* Slide-out Panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[400px] bg-gray-950 border-l border-[#3A352F] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2A26]">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#C2714F]" />
            <h2 className="text-lg font-semibold text-white">Ask Agentomy</h2>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-[#A8A29E] hover:text-white transition-colors"
            aria-label="Close help panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Guide Reader View */}
        {guideContent !== null ? (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <button
              onClick={() => { setGuideContent(null); setGuideTitle(''); }}
              className="flex items-center gap-1 text-xs text-[#C2714F] hover:text-[#D4825F] mb-3"
            >
              <ArrowLeft className="w-3 h-3" /> Back to help
            </button>
            <h3 className="text-sm font-semibold text-white mb-3">{guideTitle}</h3>
            {guideLoading ? (
              <p className="text-[#A8A29E] text-sm">Loading guide...</p>
            ) : (
              <div className="text-sm text-[#A8A29E] whitespace-pre-line leading-relaxed">
                {guideContent}
              </div>
            )}
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              {/* Guides Section */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1.5">Guides</p>
                <div className="space-y-1">
                  {GUIDES.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => openGuide(g)}
                      className="w-full text-left px-3 py-2 bg-gray-900 border border-[#2E2A26] rounded hover:border-[#C2714F] hover:bg-[#292524] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-3.5 h-3.5 text-[#C2714F] flex-shrink-0" />
                        <div>
                          <div className="text-sm text-[#D4825F]">{g.title}</div>
                          <div className="text-[10px] text-gray-500">{g.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-[#A8A29E] text-sm">
                Or ask a question about Agentomy's governance platform, pipeline, API, and capabilities.
              </p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1.5">Platform</p>
                  <div className="space-y-1">
                    {QUICK_ACTIONS.slice(0, 3).map((q) => (
                      <button key={q} onClick={() => handleSubmit(q)} className="w-full text-left px-3 py-1.5 text-sm text-[#D4825F] bg-gray-900 border border-[#2E2A26] rounded hover:border-[#C2714F] hover:bg-[#292524] transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1.5">Integration</p>
                  <div className="space-y-1">
                    {QUICK_ACTIONS.slice(3, 8).map((q) => (
                      <button key={q} onClick={() => handleSubmit(q)} className="w-full text-left px-3 py-1.5 text-sm text-[#D4825F] bg-gray-900 border border-[#2E2A26] rounded hover:border-[#C2714F] hover:bg-[#292524] transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1.5">Features</p>
                  <div className="space-y-1">
                    {QUICK_ACTIONS.slice(8).map((q) => (
                      <button key={q} onClick={() => handleSubmit(q)} className="w-full text-left px-3 py-1.5 text-sm text-[#D4825F] bg-gray-900 border border-[#2E2A26] rounded hover:border-[#C2714F] hover:bg-[#292524] transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
              <div
                className={
                  msg.role === 'user'
                    ? 'bg-[#7A3E28]/30 border border-[#7A3E28]/50 text-[#F5F0E8] rounded-lg px-3 py-2 max-w-[85%] text-sm'
                    : 'bg-gray-900 border border-[#2E2A26] text-gray-200 rounded-lg px-3 py-2 text-sm whitespace-pre-line'
                }
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        )}

        {/* Follow-up suggestions after conversation started */}
        {guideContent === null && (
        <>
        {messages.length > 0 && (
          <div className="px-5 py-2 border-t border-[#2E2A26] flex gap-1.5 flex-wrap">
            {QUICK_ACTIONS
              .filter((q) => !messages.some((m) => m.role === 'user' && m.text === q))
              .slice(0, 4)
              .map((q) => (
                <button
                  key={q}
                  onClick={() => handleSubmit(q)}
                  className="text-xs px-2 py-1 text-[#C2714F] border border-[#3A352F] rounded hover:border-[#C2714F] transition-colors"
                >
                  {q}
                </button>
              ))}
          </div>
        )}

        {/* Input */}
        <div className="px-5 py-4 border-t border-[#2E2A26]">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about Agentomy..."
              className="flex-1 bg-gray-900 border border-[#3A352F] rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#C2714F] focus:border-[#C2714F]"
            />
            <button
              onClick={() => handleSubmit(input)}
              disabled={!input.trim()}
              className="px-3 py-2 bg-[#C2714F] hover:bg-[#D4825F] disabled:bg-[#3A352F] disabled:text-gray-500 text-white rounded transition-colors"
              aria-label="Send question"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Backdrop when panel is open */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
